import { useEffect, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { getScheduledReportsList, getScheduledReportsListReset } from 'actions/reportsActions';

/**
 * Reports Schedule Data Lifecycle Hook
 *
 * Manages data fetching lifecycle and refresh logic.
 * Handles:
 * - Reset when assigned account changes
 * - Fetch data when account selected or filters change
 * - Refresh after CRUD operations (create, update, delete, enable, disable)
 * - Auto-select single account
 *
 * @param params Hook dependencies
 * @returns Void (manages side effects only)
 */
export const useReportsScheduleDataLifecycle = ({
  // Redux state
  assignedGrandfatherClientNumber,
  assignedAccountNumber,
  allAccounts,
  deleteScheduledReportLoaded,
  createScheduledReportLoaded,
  updateScheduledReportLoaded,
  enableScheduledReportLoaded,
  disableScheduledReportLoaded,
  // Account selection state
  isAccountSelected,
  selectedContractNumber,
  setIsAccountSelected,
  setSelectedContractNumber,
  setSelectedContractName,
  // Pagination/search/sort state
  pageSize,
  offset,
  sortColumn,
  sortDirection,
  searchTerm,
  setPageNumber,
  setSearchTerm,
  setSortColumn,
  setSortDirection,
  // Modal state setters
  setIsDisabled,
  // Modal close functions
  closeListModal,
  closeReportOptionsFormModal,
}: {
  assignedGrandfatherClientNumber: string;
  assignedAccountNumber: string;
  allAccounts: any[];
  deleteScheduledReportLoaded: boolean;
  createScheduledReportLoaded: boolean;
  updateScheduledReportLoaded: boolean;
  enableScheduledReportLoaded: boolean;
  disableScheduledReportLoaded: boolean;
  isAccountSelected: boolean;
  selectedContractNumber: string | null;
  setIsAccountSelected: (value: boolean) => void;
  setSelectedContractNumber: (value: string | null) => void;
  setSelectedContractName: (value: string) => void;
  pageSize: number;
  offset: number;
  sortColumn: string;
  sortDirection: string;
  searchTerm: string;
  setPageNumber: (value: number) => void;
  setSearchTerm: (value: string) => void;
  setSortColumn: (value: string) => void;
  setSortDirection: (value: string) => void;
  setIsDisabled: (value: boolean | ((prev: boolean) => boolean)) => void;
  closeListModal: () => void;
  closeReportOptionsFormModal: () => void;
}) => {
  const dispatch = useDispatch();

  const fetchScheduledReports = useCallback(() => {
    dispatch(
      getScheduledReportsList(
        assignedGrandfatherClientNumber,
        selectedContractNumber,
        pageSize,
        offset,
        sortColumn,
        sortDirection,
        searchTerm
      )
    );
  }, [
    dispatch,
    assignedGrandfatherClientNumber,
    selectedContractNumber,
    pageSize,
    offset,
    sortColumn,
    sortDirection,
    searchTerm,
  ]);

  // Keep modal close callbacks fresh without making success effects depend on unstable modal identities.
  const closeListModalRef = useRef(closeListModal);
  const closeReportOptionsFormModalRef = useRef(closeReportOptionsFormModal);

  useEffect(() => {
    closeListModalRef.current = closeListModal;
    closeReportOptionsFormModalRef.current = closeReportOptionsFormModal;
  }, [closeListModal, closeReportOptionsFormModal]);

  // Reset schedules when assigned account changes
  useEffect(() => {
    if (assignedAccountNumber) {
      dispatch(getScheduledReportsListReset());
      setSelectedContractNumber(null);
      setIsAccountSelected(false);
      setPageNumber(1);
      setSearchTerm('');
      setSortColumn('reportName');
      setSortDirection('asc');
    }
  }, [
    assignedAccountNumber,
    dispatch,
    setSelectedContractNumber,
    setIsAccountSelected,
    setPageNumber,
    setSearchTerm,
    setSortColumn,
    setSortDirection,
  ]);

  // Fetch data when account selected or pagination/search/sort changes
  useEffect(() => {
    if (isAccountSelected) {
      fetchScheduledReports();
    }
  }, [isAccountSelected, fetchScheduledReports]);

  // Refresh data after delete
  useEffect(() => {
    if (deleteScheduledReportLoaded) {
      fetchScheduledReports();
    }
  }, [deleteScheduledReportLoaded, fetchScheduledReports]);

  // Refresh data after create and close modals
  useEffect(() => {
    if (createScheduledReportLoaded) {
      fetchScheduledReports();
      closeListModalRef.current();
      closeReportOptionsFormModalRef.current();
    }
  }, [createScheduledReportLoaded, fetchScheduledReports]);

  // Refresh data after update and close modal
  useEffect(() => {
    if (updateScheduledReportLoaded) {
      fetchScheduledReports();
      closeReportOptionsFormModalRef.current();
    }
  }, [updateScheduledReportLoaded, fetchScheduledReports]);

  // Toggle disabled state and refresh after enable
  useEffect(() => {
    if (enableScheduledReportLoaded) {
      setIsDisabled((prevIsDisabled) => !prevIsDisabled);
      fetchScheduledReports();
    }
  }, [enableScheduledReportLoaded, setIsDisabled, fetchScheduledReports]);

  // Toggle disabled state and refresh after disable
  useEffect(() => {
    if (disableScheduledReportLoaded) {
      setIsDisabled((prevIsDisabled) => !prevIsDisabled);
      fetchScheduledReports();
    }
  }, [disableScheduledReportLoaded, setIsDisabled, fetchScheduledReports]);

  // Auto-select account if only one exists
  useEffect(() => {
    if (allAccounts?.length === 1) {
      setIsAccountSelected(true);
      setSelectedContractNumber(allAccounts[0].contractNumber);
      setSelectedContractName(allAccounts[0].contractName);
    }
  }, [allAccounts, setIsAccountSelected, setSelectedContractNumber, setSelectedContractName]);
};
